import { TimesheetType } from "../interfaces/TimesheetInterface";
import { getFormattedOffset } from "../util/DateAndTimeUtil";
import { _createAlert } from "./apiUtils";
import client from './client';
import { PublicClientApplication } from '@azure/msal-browser';
import config from '../configLoader';
import { PayPeriod } from "../interfaces/PayPeriodInterface";
import { JobStartResponse, JobStatus, JobStatusEnum } from "../interfaces/V2Interface";
import { getJobStatus } from "./vistaBatchExport";

const msalInstance = new PublicClientApplication(config.msalConfig);

const waitFor = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const ensureNotAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new Error("Batch print download canceled.");
  }
};

const startTimecardBatchAllJob = async (payPeriodID: number, locationID?: number): Promise<JobStartResponse> => {
  const body: { payPeriodID: number; locationID?: number } = { payPeriodID };
  if (locationID !== undefined) body.locationID = locationID;
  const response = await client.post("/admin/timesheets/generateTimecardBatchJob", body);
  return response.data as JobStartResponse;
};

const downloadTimecardBatchAllJob = async (jobID: number): Promise<{ blob: Blob; fileName: string | null }> => {
  const response = await client.get(`/admin/timesheets/generateTimecardBatchJob/${jobID}/download`, {
    responseType: "blob",
  });
  const disposition: string | undefined = response.headers["content-disposition"];
  let fileName: string | null = null;
  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    if (match) fileName = match[2].trim();
  }
  return { blob: response.data as Blob, fileName };
};

// Function to unlock a timesheet
export const unlockTimesheet = async (timesheetID: number, adminEmployeeID: number) => {
    try {
      // Ensure that the timesheetID and adminEmployeeID are valid
      if (!timesheetID || !adminEmployeeID) {
        throw new Error("Invalid timesheetID or adminEmployeeID");
      }

      // Make the API request to unlock the timesheet
      const response = await client.post('/admin/timesheet/unlock', 
        { timesheetID, adminEmployeeID },
      );
  
      // Return the response data
      return response;
  
    } catch (error) {
      // Log and alert the error if the timesheet unlocking fails
      console.error("Error unlocking timesheet:", error);
      _createAlert('Failed to unlock timesheet.', `${error}`);
      throw new Error("Failed to unlock timesheet");
    }
};

// Function to approve a timesheet
export const approveTimesheet = async (timesheetID: number, adminEmployeeID: number, type: TimesheetType) => {
    try {
      // Ensure that the timesheetID and adminEmployeeID are valid
      if (!timesheetID || !adminEmployeeID) {
        throw new Error("Invalid timesheetID or adminEmployeeID");
      }

      // Map the enum values to their corresponding endpoints
      const endpointMap: { [key in TimesheetType]: string } = {
        [TimesheetType.Unsubmitted]: "/admin/timesheets",
        [TimesheetType.ManagerPending]: "/admin/timesheets/managerPending/approve",
        [TimesheetType.Submitted]: "/admin/timesheet/supervisor/approve",
        [TimesheetType.SuperApproved]: "/admin/timesheet/payroll/approve",
        [TimesheetType.PayApproved]: "/admin/timesheets"
      };

      // Use the enum to determine the appropriate endpoint
      const endpoint = endpointMap[type] || "/admin/timesheets";
  
      // Get the current timestamp and convert it to ISO 8601 format
      const approvalTimestamp = new Date();
      const approvalDateTime = approvalTimestamp.toISOString();
  
      // Get the time zone offset for the approval timestamp
      const offset = getFormattedOffset(approvalTimestamp);
  
      // Make the API request to approve the timesheet
      const response = await client.post(endpoint, 
        { 
          timesheetID, 
          adminEmployeeID, 
          approvalDateTime,
          approvalDateTimeOffset: offset
        }
      );
  
      // Log the API response for debugging
      console.log("API Response:", response.data);
  
      // Return the response data
      return response;
  
    } catch (error) {
      // Log the error if the timesheet approval fails
      console.error("Error approving timesheet:", error);
      throw new Error("Failed to approve timesheet");
    }
};

// Function to approve a manager approval for a timesheet
export const approveManagerApproval = async (timesheetID: number, adminEmployeeID: number, requiredManagerApprovalsID: number) => {
    try {
      // Ensure that the timesheetID and adminEmployeeID are valid
      if (!timesheetID || !adminEmployeeID || !requiredManagerApprovalsID) {
        throw new Error("Invalid timesheetID, adminEmployeeID, or requiredManagerApprovalsID");
      }

      // Get the current timestamp and convert it to ISO 8601 format
      const approvalTimestamp = new Date();
      const approvalDateTime = approvalTimestamp.toISOString();
  
      // Get the time zone offset for the approval timestamp
      const offset = getFormattedOffset(approvalTimestamp);
  
      // Make the API request to approve the manager approval
      const response = await client.post('/admin/timesheet/manager/approve', 
        { 
          timesheetID, 
          adminEmployeeID, 
          requiredManagerApprovalsID,
          approvalDateTime,
          approvalDateTimeOffset: offset
        }
      );
  
      // Log the API response for debugging
      console.log("API Response:", response.data);
  
      // Return the response data
      return response;
  
    } catch (error) {
      // Log the error if the manager approval fails
      console.error("Error approving manager approval:", error);
      throw new Error("Failed to approve manager approval");
    }
};

// Function to approve a supervisor approval for a timesheet
export const approveSupervisorApproval = async (timesheetID: number, adminEmployeeID: number, requiredSuperApprovalsID?: number) => {
    try {
      // Ensure that the timesheetID and adminEmployeeID are valid
      if (!timesheetID || !adminEmployeeID) {
        throw new Error("Invalid timesheetID or adminEmployeeID");
      }

      // Get the current timestamp and convert it to ISO 8601 format
      const approvalTimestamp = new Date();
      const approvalDateTime = approvalTimestamp.toISOString();
  
      // Get the time zone offset for the approval timestamp
      const offset = getFormattedOffset(approvalTimestamp);
  
      // Prepare the request body
      const requestBody: any = {
        timesheetID, 
        adminEmployeeID,
        approvalDateTime,
        approvalDateTimeOffset: offset
      };

      // Add requiredSuperApprovalsID if provided
      if (requiredSuperApprovalsID) {
        requestBody.requiredSuperApprovalsID = requiredSuperApprovalsID;
      }
  
      // Make the API request to approve the supervisor approval
      const response = await client.post('/admin/timesheet/supervisor/approve', requestBody);
  
      // Log the API response for debugging
      console.log("API Response:", response.data);
  
      // Return the response data
      return response;
  
    } catch (error) {
      // Log the error if the supervisor approval fails
      console.error("Error approving supervisor approval:", error);
      throw new Error("Failed to approve supervisor approval");
    }
};

// Function to approve an approval group for a timesheet
export const approveApprovalGroupApproval = async (timesheetID: number, adminEmployeeID: number, requiredApprovalGroupsID: number) => {
    try {
      // Ensure that the timesheetID and adminEmployeeID are valid
      if (!timesheetID || !adminEmployeeID || !requiredApprovalGroupsID) {
        throw new Error("Invalid timesheetID, adminEmployeeID, or requiredApprovalGroupsID");
      }

      // Get the current timestamp and convert it to ISO 8601 format
      const approvalTimestamp = new Date();
      const approvalDateTime = approvalTimestamp.toISOString();

      // Get the time zone offset for the approval timestamp
      const offset = getFormattedOffset(approvalTimestamp);

      // Make the API request to approve the approval group
      const response = await client.post('/admin/timesheet/approvalGroup/approve',
        {
          timesheetID,
          adminEmployeeID,
          requiredApprovalGroupsID,
          approvalDateTime,
          approvalDateTimeOffset: offset
        }
      );

      // Log the API response for debugging
      console.log("API Response:", response.data);

      // Return the response data
      return response;

    } catch (error) {
      // Log the error if the approval group approval fails
      console.error("Error approving approval group:", error);
      throw new Error("Failed to approve approval group");
    }
};

// Shared shape for the approve/rate endpoints that report business-rule failures as a
// HTTP-200 body { success: false, errorMessage } (e.g. the sick hourly-rate validation and
// the WP6 payroll-approve block) rather than an HTTP error.
export interface ApproveResult {
  success: boolean;
  errorMessage?: string;
  /**
   * TAS-679 (WCDP Modified Duty) — /supervisor/approve sends these only when the timesheet
   * has 2+ required supervisor slots. `waiting` = this approval filled (or found already
   * filled) a slot but another is still open, so the timesheet stays Submitted.
   */
  waiting?: boolean;
  alreadyApproved?: boolean;
  requiredSuperApprovalsID?: number;
  superSlotsApproved?: number;
  superSlotsTotal?: number;
  pendingSuperNames?: string[];
}

/**
 * TAS-679 (WCDP Modified Duty) — approve ONE required supervisor slot, e.g. HR's on her
 * behalf. Same endpoint and body as approveSupervisorApproval, but returns the response
 * body and lets an axios error propagate, so the caller can show the server's reason
 * (a 403 for a role that may not approve on behalf) instead of a generic failure.
 */
export const approveSupervisorSlot = async (
  timesheetID: number,
  adminEmployeeID: number,
  requiredSuperApprovalsID: number
): Promise<ApproveResult> => {
  const approvalTimestamp = new Date();
  const response = await client.post('/admin/timesheet/supervisor/approve', {
    timesheetID,
    adminEmployeeID,
    approvalDateTime: approvalTimestamp.toISOString(),
    approvalDateTimeOffset: getFormattedOffset(approvalTimestamp),
    requiredSuperApprovalsID,
  });
  return response.data as ApproveResult;
};

// Function to update the claimed amount for a sick claim.
// Rate-validation failures (WP7: rate <= 0, below base rate, etc.) come back as HTTP 200
// with { success: false, errorMessage }, so callers must inspect the returned `success`.
export const updateSickClaimAmount = async (
  employeeSickClaimIDs: number[],
  hourlyRate: number
): Promise<ApproveResult> => {
  try {
    // Validate input
    if (!employeeSickClaimIDs || isNaN(Number(hourlyRate))) {
      throw new Error("Invalid employeeSickClaimID");
    }

    if (hourlyRate === undefined || isNaN(Number(hourlyRate))) {
      throw new Error("Invalid amountClaimed");
    }

    // Make the API request
    const response = await client.post("/admin/timesheet/tasks/updateSickClaimAmounts", {
      employeeSickClaimIDs,
      hourlyRate,
    });

    // Return the response data
    return response.data as ApproveResult;

  } catch (error) {
    console.error("Error updating sick claim amount:", error);
    _createAlert("Failed to update sick claim amount.", `${error}`);
    throw new Error("Failed to update sick claim amount");
  }
};

// ── Suggested sick hourly rate (CA Labor Code 246(l)(3) 90-day lookback) ──────────
// Mirrors SuggestedSickRateResponse in API/src/util/sickRateLookback/sickRateLookbackTypes.ts.

/** One pay period inside the lookback window, included in the rate or not. */
export interface SickRatePeriodResult {
  payPeriodID: number;
  altPayPeriodID: string | null;
  startDate: string;            // YYYY-MM-DD
  endDate: string;              // YYYY-MM-DD
  weekNumber: number;
  included: boolean;
  excludedReason?: 'no_timesheet' | 'not_payroll_approved' | 'processing_error';
  wages: number;
  hoursWorked: number;
  /** How much of `wages` is OT / DT premium. Included in the rate, shown for context. */
  otPremiumIncluded: number;
  dtPremiumIncluded: number;
  /** Prior sick pay removed from `wages`. */
  sickWagesExcluded: number;
  /** PW + union + union wage premium + catch-up fringe cash removed from `wages`. */
  fringeExcluded: number;
}

export interface SuggestedSickRate {
  success: boolean;
  errorMessage?: string;
  /** 'disabled' = the feature is off for this client; render nothing. */
  method: 'lookback' | 'base_fallback' | 'disabled';
  anchorDate?: string;
  windowStart?: string;
  windowEnd?: string;
  /** Number of complete prior pay periods counted (13 for WCDP). */
  lookbackPeriods?: number;
  totals?: {
    wages: number;
    hoursWorked: number;
    periodsIncluded: number;
    periodsExcluded: number;
  };
  computedRate?: number | null;
  baseRate?: number | null;
  suggestedRate?: number;
  suggestedSource?: 'lookback' | 'base';
  insufficientHistory?: boolean;
  insufficientHistoryReason?: string;
  periods?: SickRatePeriodResult[];
}

// Suggestion only - nothing is saved here; payroll still applies the rate through
// updateSickClaimAmount. Unlike the other calls in this file this never throws: a
// suggestion is a nice-to-have, so failures degrade to { success: false } and the
// caller simply renders nothing.
export const getSuggestedSickRate = async (timesheetID: number): Promise<SuggestedSickRate> => {
  try {
    const response = await client.get("/admin/timesheet/tasks/suggestedSickRate", {
      params: { timesheetID },
    });
    return response.data as SuggestedSickRate;
  } catch (error) {
    console.error("Error fetching suggested sick rate:", error);
    return { success: false, method: 'disabled', errorMessage: `${error}` };
  }
};

// Payroll-approve a timesheet that is already Supervisor Approved (payroll pending).
// This is the transition the single-hop approveToPayroll cannot perform (that endpoint only
// accepts Submitted timesheets). The server enforces the WP6 sick-rate block here and reports
// it as a HTTP-200 body { success: false, errorMessage }, so the caller must inspect `success`.
export const payrollApproveTimesheet = async (
  timesheetID: number,
  adminEmployeeID: number
): Promise<ApproveResult> => {
  const approvalTimestamp = new Date();
  const response = await client.post("/admin/timesheet/payroll/approve", {
    timesheetID,
    adminEmployeeID,
    approvalDateTime: approvalTimestamp.toISOString(),
    approvalDateTimeOffset: getFormattedOffset(approvalTimestamp),
  });
  return response.data as ApproveResult;
};

// Function to open the timesheet print preview in a new tab
export const openPrintPreview = async (employeeID: number, payPeriodID: number, altID: string, payPeriod: PayPeriod) => {
  try {
    if (!employeeID || !payPeriodID) {
      _createAlert("Print preview failed.", "Missing employeeID or payPeriodID");
      return;
    }

    console.log(employeeID, payPeriodID);

    const response = await client.get("/admin/timesheets/generateTimecard", {
      params: { employeeID, payPeriodID },
      responseType: "arraybuffer", // More flexible than "blob"
    });

    // Check if it's a valid PDF (by inspecting first few bytes)
    const byteArray = new Uint8Array(response.data);
    const header = new TextDecoder().decode(byteArray.slice(0, 4));
    if (header !== "%PDF") {
      const text = new TextDecoder().decode(byteArray);
      console.error("Expected PDF, got:", text.slice(0, 300));
      _createAlert("Print preview failed.", "Server did not return a valid PDF.");
      return;
    }

    // All good — create and download Blob
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    const weekNumber = payPeriod?.WeekNumber || '';
    const year = payPeriod?.AccountingYear || '';
    a.download = `${altID}_WK${weekNumber}_${year}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return true;

  } catch (error) {
    console.error("Error downloading PDF:", error);
    return false;
  }
};

export const openBatchPrintPreview = async (supervisorID: number, payPeriodID: number, altSupervisorID: string, payPeriod: PayPeriod) => {
  try {
    console.log("Requesting batch PDF for:", { supervisorID, payPeriodID });
    
    const response = await client.get("/admin/timesheets/generateTimecardBatch", {
      params: { supervisorID, payPeriodID },
      responseType: "arraybuffer",
    });

    console.log("Response headers:", response.headers);
    console.log("Response data length:", response.data.byteLength);

    // Check the first few bytes to verify it's a PDF
    const byteArray = new Uint8Array(response.data);
    console.log("First 10 bytes:", byteArray.slice(0, 10));

    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    
    // Open in new tab for debugging
    window.open(blobUrl, '_blank');
    
    // Also proceed with download
    const a = document.createElement("a");
    a.href = blobUrl;
    const weekNumber = payPeriod?.WeekNumber || '';
    const year = payPeriod?.AccountingYear || '';
    a.download = `${altSupervisorID}_WK${weekNumber}_${year}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 100);

    return true;
  } catch (error) {
    console.error("Batch PDF error:", error);
    }
    return false;
};

export const openBatchPrintPreviewAll = async (
  payPeriodID: number,
  payPeriod: PayPeriod,
  signal?: AbortSignal,
  onProgress?: (jobStatus: JobStatus) => void,
  locationID?: number,
  locationLabel?: string
) => {
  try {
    ensureNotAborted(signal);
    const { jobID } = await startTimecardBatchAllJob(payPeriodID, locationID);

    while (true) {
      ensureNotAborted(signal);
      const jobStatus = await getJobStatus(jobID);
      onProgress?.(jobStatus);

      if (jobStatus.status === JobStatusEnum.COMPLETED) {
        const { blob, fileName } = await downloadTimecardBatchAllJob(jobID);
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        const weekNumber = payPeriod?.WeekNumber || "";
        const year = payPeriod?.AccountingYear || "";
        const fallbackName = locationLabel
          ? `${locationLabel}_WK${weekNumber}_${year}.pdf`
          : `AllTimesheets_WK${weekNumber}_${year}.pdf`;
        a.download = fileName ?? fallbackName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        return true;
      }

      if (jobStatus.status === JobStatusEnum.FAILED) {
        const resultData = jobStatus.resultData as { error?: string; message?: string } | undefined;
        const errorMessage = resultData?.error || resultData?.message || "Timecard batch generation failed.";
        const failureError = new Error(errorMessage);
        (failureError as Error & { resultData?: unknown }).resultData = jobStatus.resultData;
        throw failureError;
      }

      await waitFor(5000);
    }
  } catch (error) {
    if (signal?.aborted) {
      return false;
    }

    console.error("Batch ALL PDF error:", error);
    throw error;
  }
};

// Function to unlock a timesheet
export const refreshTimesheet = async (employeeID: number, payPeriodID: number) => {
  try {
    // Ensure that the timesheetID and adminEmployeeID are valid
    if (!employeeID || !payPeriodID) {
      throw new Error("Invalid timesheetID or adminEmployeeID");
    }

    // Make the API request to unlock the timesheet
    const response = await client.get('/admin/timesheets/getEmployeeTimesheet', {
      params: { employeeID, payPeriodID }
    });

    // Return the response data
    return response.data.days;

  } catch (error) {
    // Log and alert the error if the timesheet unlocking fails
    console.error("Error refreshing timesheet:", error);
    _createAlert('Failed to refresh timesheet.', `${error}`);
    throw new Error("Failed to refresh timesheet");
  }
};

// Function to update supervisor for a timesheet
export const updateTimesheetSupervisor = async (timesheetID: number, supervisorID: number, adminEmployeeID: number) => {
    try {
      // Ensure that all required parameters are valid
      if (!timesheetID || !supervisorID || !adminEmployeeID) {
        throw new Error("Invalid timesheetID, supervisorID, or adminEmployeeID");
      }

      // Make the API request to update the supervisor
      const response = await client.put('/admin/timesheet/updateSupervisor', {
        timesheetID,
        supervisorID,
        adminEmployeeID
      });
  
      // Log the API response for debugging
      console.log("Update Supervisor Response:", response.data);
  
      // Return the response data
      return response;
  
    } catch (error) {
      // Log the error if the supervisor update fails
      console.error("Error updating supervisor:", error);
      throw new Error("Failed to update supervisor");
    }
};

